import type { Distance, SpatialRectangle } from "@pdfking/math"
import type { Page, PageNumber } from "@pdfking/renderer-api"
import type { TaskContext } from "@pdfking/taskmaker"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import type { FilePlottingContext } from "../types/tasks/FilePlottingContext"
import { BlobArray } from "@pdfking/compatibility"
import { CreateCompletePage, CreateContext } from "@pdfking/renderer-api"
import { PDFPlot } from "@pdfking/renderer-default"
import { IterableToArray, RecordMap } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

export const TaskPlot = async function* ({
  emit,
  engine,
  options,
  page: taskPage,
  context: taskContext,
}: TaskContext<
  EngineTasks,
  {
    readonly options: PdfEngineOptions
    readonly page: Page
    readonly context: FilePlottingContext
  }
>): AsyncGenerator<SinglePagePdf> {
  const emitter = (done: number) =>
    emit({
      done,
      name: TrackedTaskName.PLOT_PDF,
      total: 1,
    })

  void emitter(0)

  const context = CreateContext({
    ...options,
    ...taskContext,
    file_map: RecordMap(
      file =>
        RequestBlob(engine, options, file)
          .then(({ stream }) => IterableToArray(stream))
          .then(BlobArray),
      taskContext.file_map || {}
    ),
  })
  const page = CreateCompletePage(taskPage, context)

  const content = await GenerateFile(
    engine,
    options,
    PDFPlot([page], context),
    ContentType.PDF,
    emit
  )

  const bleed = page.bleed
  const width = (page.width + bleed * 2) as Distance
  const height = (page.height + bleed * 2) as Distance

  const trim_box: SpatialRectangle<Distance> = {
    height: page.height,
    width: page.width,
    x: bleed,
    y: bleed,
  }

  const media_box: SpatialRectangle<Distance> = {
    height,
    width,
    x: 0 as Distance,
    y: 0 as Distance,
  }

  yield {
    content,
    context: taskContext,
    enum: VirtualFileType.SINGLE_PAGE_PDF,
    file_meta: {},
    media_box,
    page,
    page_count: 1 as PageNumber,
    page_number: 1 as PageNumber,
    size: media_box,
    source: undefined,
    trim_box,
  } as SinglePagePdf

  void emitter(1)
}
